import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, Medal, Crown, Filter, ChevronDown, UserCog, Users, UserCircle } from 'lucide-react';
import { AVP, Director, LoanOfficerWithContext } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { MovementIndicator } from './MovementIndicator';
interface LeaderboardSectionProps {
  avps: AVP[];
  directors: Director[];
  loanOfficers: LoanOfficerWithContext[];
  onSelectAvp?: (avp: AVP) => void;
  onSelectDirector?: (director: Director) => void;
  onSelectLoanOfficer?: (lo: LoanOfficerWithContext) => void;
}
type ViewMode = 'avp' | 'director' | 'lo';
// Unified leaderboard item shape — works for AVPs, Directors, and Loan Officers
interface LeaderboardItem {
  id: string;
  name: string;
  initials: string;
  division: string;
  loCount?: number;
  loansClosed: number;
  loanVolume: number;
  totalRevenue: number;
  avgRevenue: number;
  // Optional — only AVPs have ranking history
  previousRank?: number;
  // Back-references to the source entity for click handling
  sourceAvp?: AVP;
  sourceDirector?: Director;
  sourceLoanOfficer?: LoanOfficerWithContext;
  displayRank: number;
  // Display-only subtitle for the card
  subtitle: string;
}
export function LeaderboardSection({
  avps,
  directors,
  loanOfficers,
  onSelectAvp,
  onSelectDirector,
  onSelectLoanOfficer
}: LeaderboardSectionProps) {
  const [view, setView] = useState<ViewMode>('avp');
  const [divisionFilter, setDivisionFilter] = useState<string>('All');
  // For the LO view we paginate: top 3 podium + 7 rest = 10 total, with Show More
  const LO_INITIAL_REST = 7;
  const LO_PAGE_SIZE = 10;
  const [loVisibleRest, setLoVisibleRest] = useState<number>(LO_INITIAL_REST);
  // Reset pagination when switching to LO or changing division filter
  useEffect(() => {
    setLoVisibleRest(LO_INITIAL_REST);
  }, [view, divisionFilter]);
  // Normalize current view into a unified item list
  const items: LeaderboardItem[] = useMemo(() => {
    if (view === 'avp') {
      return avps.filter(a => divisionFilter === 'All' || a.division === divisionFilter).map(a => ({
        id: a.id,
        name: a.name,
        initials: a.initials,
        division: a.division,
        loCount: a.loCount,
        loansClosed: a.loansClosed,
        loanVolume: a.loanVolume,
        totalRevenue: a.totalRevenue,
        avgRevenue: a.avgRevenue,
        previousRank: a.previousRank,
        sourceAvp: a,
        displayRank: 0,
        subtitle: `${a.division} Division`
      }));
    }
    if (view === 'director') {
      return directors.filter(d => divisionFilter === 'All' || d.division === divisionFilter).map(d => ({
        id: d.id,
        name: d.name,
        initials: d.initials,
        division: d.division ?? '—',
        loCount: d.loCount,
        loansClosed: d.loansClosed,
        loanVolume: d.loanVolume,
        totalRevenue: d.totalRevenue,
        avgRevenue: d.avgRevenue,
        sourceDirector: d,
        displayRank: 0,
        subtitle: d.avpName ? `Reports to ${d.avpName}` : 'Director'
      }));
    }
    return loanOfficers.filter(lo => divisionFilter === 'All' || lo.division === divisionFilter).map(lo => ({
      id: lo.id,
      name: lo.name,
      initials: lo.initials,
      division: lo.division,
      loansClosed: lo.loansClosed,
      loanVolume: lo.loanVolume,
      totalRevenue: lo.totalRevenue,
      avgRevenue: lo.avgRevenue,
      sourceLoanOfficer: lo,
      displayRank: 0,
      subtitle: `${lo.directorName} · ${lo.avpName}`
    }));
  }, [view, avps, directors, loanOfficers, divisionFilter]);
  // Ranking metric depends on view: AVP/Director by avgRevenue, LO by totalRevenue
  const rankingMetric: 'avgRevenue' | 'totalRevenue' = view === 'lo' ? 'totalRevenue' : 'avgRevenue';
  const sorted = useMemo(() => {
    const arr = [...items].sort((a, b) => b[rankingMetric] - a[rankingMetric]);
    return arr.map((it, i) => ({
      ...it,
      displayRank: i + 1
    }));
  }, [items, rankingMetric]);
  const top3 = sorted.slice(0, 3);
  const fullRest = sorted.slice(3);
  const rest = view === 'lo' ? fullRest.slice(0, loVisibleRest) : fullRest;
  const hasMore = view === 'lo' && fullRest.length > rest.length;
  const getMetricValue = (item: LeaderboardItem) => formatCurrency(item[rankingMetric]);
  const getMetricLabel = () => rankingMetric === 'totalRevenue' ? 'Total Revenue' : 'Avg Revenue';
  const handleClick = (item: LeaderboardItem) => {
    if (item.sourceAvp) onSelectAvp?.(item.sourceAvp);else if (item.sourceDirector) onSelectDirector?.(item.sourceDirector);else if (item.sourceLoanOfficer) onSelectLoanOfficer?.(item.sourceLoanOfficer);
  };
  return <section className="mb-12" data-id="element-315">
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4" data-id="element-316">
        <div data-id="element-317">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2" data-id="element-318">
            <Trophy className="w-5 h-5 text-indigo-400" data-id="element-319" />
            Monthly Leaderboard
          </h2>
          <p className="text-sm text-slate-400 mt-1" data-id="element-320">
            {view === 'avp' ? 'Top performing AVPs ranked by avg revenue' : view === 'director' ? 'Top performing Directors ranked by avg revenue' : 'Top performing Loan Officers ranked by total revenue'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3" data-id="element-321">
          {/* View toggle: AVP / Director / LO */}
          <div className="glass-panel rounded-lg p-1 flex text-sm" data-id="element-322">
            {([{
            id: 'avp',
            label: 'AVPs',
            Icon: UserCog
          }, {
            id: 'director',
            label: 'Directors',
            Icon: Users
          }, {
            id: 'lo',
            label: 'Loan Officers',
            Icon: UserCircle
          }] as {
            id: ViewMode;
            label: string;
            Icon: typeof UserCog;
          }[]).map(v => <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1.5 ${view === v.id ? 'bg-indigo-500/20 text-indigo-300 font-medium' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-323">
                <v.Icon className="w-3.5 h-3.5" data-id="element-324" />
                {v.label}
              </button>)}
          </div>

          {/* Division filter */}
          <div className="relative group" data-id="element-325">
            <button className="glass-panel px-3 py-2 rounded-lg flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-id="element-326">
              <Filter className="w-4 h-4" data-id="element-327" />
              {divisionFilter}
              <ChevronDown className="w-4 h-4" data-id="element-328" />
            </button>
            <div className="absolute right-0 top-full mt-2 w-40 bg-[#141a2c] border border-white/10 shadow-xl rounded-xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20" data-id="element-329">
              {['All', 'Northeast', 'Southeast', 'Central', 'West', 'Mountain'].map(div => <button key={div} onClick={() => setDivisionFilter(div)} className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/10 rounded-lg transition-colors" data-id="element-330">
                  {div}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Spotlight - Podium order: 2nd, 1st, 3rd */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 md:items-end" data-id="element-331">
        {[top3[1], top3[0], top3[2]].filter(Boolean).map(item => {
        const rank = item.displayRank;
        const isFirst = rank === 1;
        const isSecond = rank === 2;
        const cardStyle = isFirst ? 'bg-gradient-gold shadow-[0_0_30px_rgba(250,204,21,0.15)]' : isSecond ? 'bg-gradient-silver' : 'bg-gradient-bronze';
        const textStyle = isFirst ? 'text-gradient-gold' : isSecond ? 'text-gradient-silver' : 'text-gradient-bronze';
        const Icon = isFirst ? Crown : Medal;
        return <button key={item.id} onClick={() => handleClick(item)} className={`rounded-3xl p-6 relative overflow-hidden flex flex-col items-center text-center border text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/40 ${cardStyle} ${isFirst ? 'md:-translate-y-8' : isSecond ? 'md:-translate-y-3' : ''}`} data-id="element-332">
              {/* Rank Badge */}
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center font-bold text-white border border-white/10 backdrop-blur-md" data-id="element-333">
                {item.displayRank}
              </div>

              {/* Movement indicator — AVPs only */}
              {view === 'avp' && item.previousRank !== undefined && <div className="absolute top-4 right-4" data-id="element-334">
                  <MovementIndicator currentRank={item.displayRank} previousRank={item.previousRank} data-id="element-335" />
                </div>}

              <div className="relative mb-4 mt-2" data-id="element-336">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold bg-[#0f1423] border-2 ${isFirst ? 'border-yellow-500/50' : isSecond ? 'border-slate-400/50' : 'border-orange-500/50'} shadow-xl`} data-id="element-337">
                  {item.initials}
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#0f1423] border border-white/10 shadow-lg" data-id="element-338">
                  <Icon className={`w-4 h-4 ${isFirst ? 'text-yellow-400' : isSecond ? 'text-slate-300' : 'text-orange-400'}`} data-id="element-339" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-white mt-2" data-id="element-340">{item.name}</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-341">{item.subtitle}</p>

              <div className="w-full bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-342">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-343">
                  {getMetricLabel()}
                </p>
                <p className={`text-3xl font-bold ${textStyle}`} data-id="element-344">
                  {getMetricValue(item)}
                </p>
              </div>

              <div className="w-full grid grid-cols-2 gap-2 mt-4" data-id="element-345">
                <div className="bg-black/10 rounded-lg p-2 border border-white/5" data-id="element-346">
                  <p className="text-xs text-slate-500" data-id="element-347">Loans</p>
                  <p className="text-sm font-semibold text-slate-200" data-id="element-348">
                    {formatNumber(item.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/10 rounded-lg p-2 border border-white/5" data-id="element-349">
                  <p className="text-xs text-slate-500" data-id="element-350">
                    {view === 'lo' ? 'Avg Rev' : 'LOs'}
                  </p>
                  <p className="text-sm font-semibold text-slate-200" data-id="element-351">
                    {view === 'lo' ? formatCurrency(item.avgRevenue) : item.loCount ?? 0}
                  </p>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Remaining Ranks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-352">
        {rest.map(item => <button key={item.id} onClick={() => handleClick(item)} className="glass-panel rounded-2xl p-4 flex items-center gap-4 text-left w-full cursor-pointer hover:border-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40" data-id="element-353">
            <div className="w-8 text-center font-bold text-slate-500" data-id="element-354">
              {item.displayRank}
            </div>

            <div className="w-10 h-10 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-semibold text-sm shrink-0" data-id="element-355">
              {item.initials}
            </div>

            <div className="flex-1 min-w-0" data-id="element-356">
              <h4 className="text-white font-medium truncate" data-id="element-357">{item.name}</h4>
              <p className="text-xs text-slate-400 truncate" data-id="element-358">{item.subtitle}</p>
            </div>

            <div className="text-right" data-id="element-359">
              <p className="text-white font-semibold" data-id="element-360">{getMetricValue(item)}</p>
              {view === 'avp' && item.previousRank !== undefined && <div className="flex justify-end mt-1" data-id="element-361">
                  <MovementIndicator currentRank={item.displayRank} previousRank={item.previousRank} data-id="element-362" />
                </div>}
            </div>
          </button>)}
      </div>

      {/* Show More — LO view only */}
      {hasMore && <div className="flex justify-center mt-6" data-id="element-363">
          <button onClick={() => setLoVisibleRest(c => c + LO_PAGE_SIZE)} className="glass-panel px-5 py-2.5 rounded-lg text-sm text-slate-200 hover:text-white hover:border-white/20 transition-colors flex items-center gap-2" data-id="element-364">
            <ChevronDown className="w-4 h-4" data-id="element-365" />
            Show {Math.min(LO_PAGE_SIZE, fullRest.length - rest.length)} more
            <span className="text-slate-500" data-id="element-366">
              ({rest.length + 3} of {sorted.length})
            </span>
          </button>
        </div>}
    </section>;
}